import { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  get,
  find,
  size,
  isArray,
  isEqual,
} from 'lodash';
import {
  setPanel,
} from '../../actions/Global/GlobalActions';
import {
  queueToast,
} from '../../actions/Toast/ToastActions';
import {
  group,
} from '../../helpers/Location';
import Location from '../../models/ExpandedLocations/Location';
import Company from '../../models/ExpandedLocations/Company';
import API from '../../api';
import AssignObject from './AssignObject';
import Dropdown from '../Dropdown/Dropdown';
import Field from '../Field/Field2';
import TableLocations from '../VibeTable/Tables/TableLocations';
import TableCompanies from '../VibeTable/Tables/TableCompanies';
import VibeButton from '../VibeButton/VibeButton';
import VibeModal from '../VibeModal/VibeModal';
import VibeIcon from '../VibeIcon/VibeIcon';
import viAdd from '../../icons/viAdd';
import viClose from '../../icons/viClose';
import viViewDayTrack from '../../icons/viViewDayTrack';
import viCalendar from '../../icons/viCalendar';
import viMicrophone from '../../icons/viMicrophone';
import viMicrophoneList from '../../icons/viMicrophoneList';
import viPlaylist from '../../icons/viPlaylist';
import viStation from '../../icons/viStation';
// import viUser from '../../icons/viUser';
import * as color from '../../sass/color.scss';
import './LocationsUnassigned.scss';

class LocationsUnassigned extends PureComponent {
  constructor(props) {
    super(props);

    const {
      user,
      type,
      companyId,
      locations,
    } = props;

    // only highlight company IDs with all locations
    const locationItems = type === 'locations'
      ? locations
      : locations.filter(location => location.locationId === '*');

    // only show the objects the user has access to
    const assignObjectItems = [];

    if (user.can('baseline.view')) {
      assignObjectItems.push({
        name: 'Baseline',
        value: 'baseline',
        icon: viViewDayTrack,
      });
    }

    if (user.can('event.view')) {
      assignObjectItems.push({
        name: 'Event',
        value: 'event',
        icon: viCalendar,
      });
    }

    if (user.can('message.view') || user.can('advertisement.view')) {
      assignObjectItems.push({
        name: 'Message',
        value: 'message',
        icon: viMicrophone,
      });
    }

    if (user.can('messagelist.view')) {
      assignObjectItems.push({
        name: 'Message Block',
        value: 'message block',
        icon: viMicrophoneList,
      });
    }

    if (user.can('playlist.view')) {
      assignObjectItems.push({
        name: 'Playlist',
        value: 'playlist',
        icon: viPlaylist,
      });
    }

    if (user.can('mix.view')) {
      assignObjectItems.push({
        name: 'Station',
        value: 'station',
        icon: viStation,
      });
    }

    // if (user.can('user.view')) {
    //   assignObjectItems.push({
    //     name: 'User',
    //     value: 'user',
    //     icon: viUser,
    //   });
    // }

    const locationFuncType = user.getLocationFunc(companyId);

    this.state = {
      highlightIds: type === 'locations'
        ? locationItems.map(location => location.locationId)
        : locationItems.map(location => location.companyId),
      locationFuncType,
      assignObjectType: 'locations',
      assignObjectItems,
      // filters the available locations table last fetched with, including the column filters
      tableFilters: {},
      showAssignRandom: false,
      randomCount: '',
      randomMode: 'add',
    };
  }

  /**
   * Get Locations Function by User Type
   */
  getLocationsFunc = () => {
    const {
      user,
      partnerId,
    } = this.props;

    if (partnerId && user.accountType !== 'partner') {
      return API.Partner.getLocations;
    }

    if (user.accountType === 'partner') {
      return API.PartnerUser.getLocations;
    }

    return API.User.getLocations;
  };

  /**
   * Get Companies Function by User Type
   */
  getCompaniesFunc = () => {
    const {
      user,
      partnerId,
    } = this.props;

    if (partnerId && user.accountType !== 'partner') {
      return API.Partner.getCompanies;
    }

    if (user.accountType === 'partner') {
      return API.PartnerUser.getCompanies;
    }

    return API.User.getCompanies;
  };

  /**
   * Get the filters used to query the available companies/locations
   */
  getFilters = () => {
    const {
      companyId,
      partnerId,
      scopeLimit = [],
      resolutionLimit = [],
      filtersUnassigned,
    } = this.props;

    const {
      locationFuncType,
    } = this.state;

    const filters = locationFuncType === 'company'
      ? {
        companyId,
        ...filtersUnassigned,
      }
      : filtersUnassigned;

    if (scopeLimit.length > 0) {
      filters.scopeLimit = group(scopeLimit);
    }

    if (resolutionLimit.length > 0) {
      filters.resolutionLimit = resolutionLimit;
    }

    if (partnerId) {
      filters.partnerId = partnerId;
    }

    return filters;
  };

  /**
   * Keep track of the filters the available locations table fetched with
   * The column filters live in the table state, so this is the only way to know what the user is looking at
   */
  onFetchCompleteLocations = ({
    filters,
  }) => {
    this.setState((state) => {
      return !isEqual(state.tableFilters, filters)
        ? {
          tableFilters: filters,
        }
        : null;
    });
  };

  /**
   * When the assign object is changed
   */
  onChangeAssignObject = (name) => {
    if (this._scrollRef) {
      this._scrollRef.scrollTop = 0;
    }

    this.setState({
      assignObjectType: name,
    });
  };

  /**
   * Assign an objects locations to this object
   */
  onAssignObjectLocations = ({
    locations,
    locationsData,
  }) => {
    const {
      onUpdate,
    } = this.props;

    onUpdate({
      locations,
      locationsData,
    });

    this.onCloseAvailable();
  };

  onAddLocation = (locationItems) => {
    const {
      onAddLocation,
    } = this.props;

    if (!isArray(locationItems)) {
      locationItems = [locationItems];
    }

    const newLocationItems = locationItems.map(item => new Location(item));

    this.setState((state) => {
      const locationIds = locationItems.map(location => location._id);

      return {
        highlightIds: [...state.highlightIds, ...locationIds],
      };
    });

    onAddLocation(newLocationItems);
    document.dispatchEvent(new Event('onUpdateTableAssignedLocations'));
  };

  onAddCompany = (companyItems) => {
    const {
      onAddCompany,
    } = this.props;

    if (!isArray(companyItems)) {
      companyItems = [companyItems];
    }

    const newCompanyItems = companyItems.map(item => new Company(item));

    this.setState((state) => {
      const companyIds = companyItems.map(company => company._id);

      return {
        highlightIds: [...state.highlightIds, ...companyIds],
      };
    });

    onAddCompany(newCompanyItems);
    document.dispatchEvent(new Event('onUpdateTableAssignedCompanies'));
  };

  /**
   * Replace every assigned location with a new set
   */
  onReplaceLocations = (locationItems) => {
    const {
      onReplaceLocations,
    } = this.props;

    const newLocationItems = locationItems.map(item => new Location(item));

    this.setState({
      highlightIds: locationItems.map(location => location._id),
    });

    onReplaceLocations(newLocationItems);
    document.dispatchEvent(new Event('onUpdateTableAssignedLocations'));
  };

  onRemoveLocation = (locationItems) => {
    const {
      onRemoveLocation,
    } = this.props;

    if (!isArray(locationItems)) {
      locationItems = [locationItems];
    }

    const locationIds = locationItems.map(location => location._id);

    this.setState((state) => {
      return {
        highlightIds: state.highlightIds.filter(id => !locationIds.includes(id)),
      };
    });

    onRemoveLocation(locationItems);
  };

  onRemoveCompany = (companyItems) => {
    const {
      onRemoveCompany,
    } = this.props;

    if (!isArray(companyItems)) {
      companyItems = [companyItems];
    }

    const companyIds = companyItems.map(company => company._id);

    this.setState((state) => {
      return {
        highlightIds: state.highlightIds.filter(id => !companyIds.includes(id)),
      };
    });

    onRemoveCompany(companyItems);
  };

  /**
   * Open the Assign Random Modal
   */
  onClickAssignRandom = () => {
    this.setState({
      showAssignRandom: true,
    });
  };

  /**
   * Cancel assigning random locations
   */
  onCancelAssignRandom = () => {
    this.setState({
      showAssignRandom: false,
      randomCount: '',
      randomMode: 'add',
    });
  };

  /**
   * Change the number of random locations to assign
   */
  onChangeRandomCount = (e) => {
    const {
      target: {
        value,
      },
    } = e;

    this.setState({
      randomCount: value,
    });
  };

  /**
   * Confirm assigning a random sample of the available locations
   */
  onConfirmAssignRandom = async () => {
    const {
      user,
      locations,
      queueToast,
    } = this.props;

    const {
      tableFilters,
      randomCount,
      randomMode,
    } = this.state;

    const sampleSize = parseInt(randomCount, 10);

    this.onCancelAssignRandom();

    queueToast({
      type: 'info',
      title: `Fetching ${sampleSize} Random ${sampleSize === 1 ? 'Location' : 'Locations'}...`,
      allowClose: true,
    });

    try {
      const getLocations = this.getLocationsFunc();

      const response = await getLocations({
        _id: user._id,
        // the API caps the sample at the page size unless all results are requested
        pageSize: -1,
        sampleSize,
        // sample from the same set the table is showing, including the filters the user applied to it
        filters: {
          active: true,
          ...this.getFilters(),
          ...tableFilters,
        },
      });

      const randomLocations = get(response, 'data', []);

      if (randomLocations.length <= 0) {
        queueToast({
          type: 'error',
          title: 'No Random Locations Found',
          timeout: 10,
          allowClose: true,
        });

        return;
      }

      // the sample can include locations that are already assigned
      const assignLocations = randomMode === 'replace'
        ? randomLocations
        : randomLocations.filter(location => !find(locations, {
          companyId: location.companyId,
          locationId: location._id,
        }));

      const numAssigned = assignLocations.length;
      const numDuplicates = randomLocations.length - numAssigned;

      if (randomMode === 'replace') {
        this.onReplaceLocations(assignLocations);
      } else {
        this.onAddLocation(assignLocations);
      }

      queueToast({
        type: numAssigned > 0
          ? 'success'
          : 'info',
        title: numDuplicates > 0
          ? `${numAssigned} Random Locations Assigned, ${numDuplicates} Already Assigned`
          : `${numAssigned} Random ${numAssigned === 1 ? 'Location' : 'Locations'} Assigned`,
        timeout: numDuplicates > 0
          ? 10
          : undefined,
        allowClose: true,
      });
    } catch (err) {
      console.error('Error fetching random locations', err);

      queueToast({
        type: 'error',
        title: 'Error Fetching Random Locations',
        timeout: 10,
        allowClose: true,
      });
    }
  };

  /**
   * Close the third panel on the sidebar
   */
  onCloseAvailable = () => {
    const {
      setPanel,
    } = this.props;

    setPanel({
      thirdPanel: {
        show: false,
      },
    });

    // Reset the children after it's been hidden
    setTimeout(() => {
      setPanel({
        thirdPanel: {
          children: null,
        },
      });
    }, 900);
  };

  render() {
    const {
      user,
      type,
      companyId,
      fetchUnassignedLocations,
      fetchUnassignedCompanies,
      filtersUnassigned,
      allowRandom,
    } = this.props;

    const {
      highlightIds,
      assignObjectType,
      assignObjectItems,
      tableFilters,
      showAssignRandom,
      randomCount,
      randomMode,
    } = this.state;

    const filters = this.getFilters();

    // default to audio columns, if mediaFormat is a filter option, assign visual or audio columns
    let mediaTypeColumns = [
      'Add/Remove',
      'Name',
      'Banner',
      'Company',
      'Category',
      'Media Format',
      'Screen Diagonal',
      'Resolution',
      'Address 1',
      'Address 2',
      'Country',
      'City',
      'State/Province',
      'Postal Code',
      'DMA',
      'Ad Network',
      'Ad Programs',
      'External IDs',
      'Tags',
      '...',
    ];

    if (filtersUnassigned.mediaFormat) {
      mediaTypeColumns = filtersUnassigned.mediaFormat === 'visual'
        ? mediaTypeColumns = [
          'Add/Remove',
          'Name',
          'Banner',
          'Company',
          'Category',
          'Media Format',
          'Screen Diagonal',
          'Resolution',
          'Address 1',
          'Address 2',
          'Country',
          'City',
          'State/Province',
          'Postal Code',
          'DMA',
          'Ad Network',
          'Ad Programs',
          'External IDs',
          'Tags',
          '...',
        ] : mediaTypeColumns = [
          'Add/Remove',
          'Name',
          'Banner',
          'Company',
          'Category',
          'Media Format',
          'Address 1',
          'Address 2',
          'Country',
          'City',
          'State/Province',
          'Postal Code',
          'DMA',
          'Ad Network',
          'Ad Programs',
          'External IDs',
          'Tags',
          '...',
        ];
    }

    const highlight = highlightIds.map((_id) => {
      return {
        _id,
      };
    });

    // require a company/banner filter so a random sample can never pull in locations
    // from a company or banner that is not allowed on the object
    const hasRandomFilter = size(tableFilters.companyId) > 0
      || size(tableFilters.bannerName) > 0;

    const assignRandomButton = (
      <VibeButton
        key="assign-random"
        text="Assign Random"
        variant="outlined"
        color="secondary"
        icon={viAdd}
        tooltip={hasRandomFilter
          ? 'Assign a random sample of the available locations'
          : 'Filter by company or banner before assigning random locations'}
        tooltipProps={{
          placement: 'top',
        }}
        disabled={!hasRandomFilter}
        testId="assign-random-button"
        onClick={this.onClickAssignRandom}
      />
    );

    return (
      <div className="LocationsUnassigned">
        <div className="title-container">
          <div
            className="title"
            style={{
              flexGrow: assignObjectItems.length <= 0
                ? 1
                : 0,
            }}
          >
            Assign {type === 'locations' ? 'Locations' : 'Companies'}
          </div>

          {assignObjectItems.length > 0 ? (
            <div
              className="assign-object"
              style={{
                flexGrow: 1,
              }}
            >
              <Dropdown
                title="Assign by Object..."
                width={156}
                themeColor={color.success}
                defaultValue="locations"
                items={assignObjectItems}
                // disabled={!allowChanges}
                onChange={this.onChangeAssignObject}
              />
            </div>
          ) : null}

          <VibeIcon
            className="close"
            icon={viClose}
            color={color.white}
            hoverColor={color.lightGray}
            size={24}
            onClick={this.onCloseAvailable}
          />
        </div>

        {assignObjectType === 'locations' ? (
          <div className="locations-content">
            {type === 'locations' ? (
              <TableLocations
                columns={mediaTypeColumns}
                mediaFormat={filtersUnassigned.mediaFormat}
                fetch={fetchUnassignedLocations ?? this.getLocationsFunc()}
                fetchProps={{
                  filters: {
                    _id: user._id,
                  },
                }}
                filters={filters}
                defaultSortBy={{
                  label: 'Name',
                  attr: 'name',
                  direction: 'asc',
                }}
                paginator
                paginatorProps={{
                  label: 'Locations',
                  urlPaging: false,
                  urlFilters: false,
                }}
                toolbarProps={{
                  customButtons: allowRandom
                    ? [assignRandomButton]
                    : [],
                }}
                menuItems={[
                  { name: 'Archive', userCan: 'location.delete' },
                ]}
                available
                highlight={highlight}
                onAdd={this.onAddLocation}
                onRemove={this.onRemoveLocation}
                onFetchComplete={this.onFetchCompleteLocations}
              />
            ) : (
              <TableCompanies
                columns={[
                  'Add/Remove',
                  'Company',
                  'Category',
                  'Address 1',
                  'Address 2',
                  'City',
                  'State',
                  'Zip',
                  'Salesforce ID',
                  'Tags',
                  '...',
                ]}
                fetch={fetchUnassignedCompanies ?? this.getCompaniesFunc()}
                fetchProps={{
                  filters: {
                    _id: user._id,
                  },
                }}
                filters={filters}
                defaultSortBy={{
                  label: 'Company',
                  attr: 'name',
                  direction: 'asc',
                }}
                paginator
                paginatorProps={{
                  label: 'Companies',
                  urlPaging: false,
                  urlFilters: false,
                }}
                menuItems={[
                  { name: 'Archive', userCan: 'company.delete' },
                ]}
                available
                highlight={highlight}
                onAdd={this.onAddCompany}
                onRemove={this.onRemoveCompany}
              />
            )}
          </div>
        ) : (
          <AssignObject
            className="object-content"
            companyId={companyId}
            type={assignObjectType}
            onAssignObjectLocations={this.onAssignObjectLocations}
          />
        )}

        <VibeModal
          className="LocationsUnassignedModal"
          type="confirm"
          show={showAssignRandom}
          confirmProps={{
            disabled: !(parseInt(randomCount, 10) > 0),
          }}
          title="Enter the number of locations needed"
          text={(
            <div>
              <Field
                type="number"
                placeholder="Number of Locations"
                value={randomCount}
                numberProps={{
                  min: 1,
                }}
                testId="assign-random-count-input"
                autoFocus
                onChange={this.onChangeRandomCount}
              />

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 16,
                  marginTop: 16,
                }}
              >
                <Field
                  type="radio"
                  label="Add to existing locations"
                  radioProps={{
                    selected: randomMode === 'add',
                  }}
                  testId="assign-random-add-option"
                  onClick={() => this.setState({ randomMode: 'add' })}
                />

                <Field
                  type="radio"
                  label="Replace all locations"
                  radioProps={{
                    selected: randomMode === 'replace',
                  }}
                  testId="assign-random-replace-option"
                  onClick={() => this.setState({ randomMode: 'replace' })}
                />
              </div>
            </div>
          )}
          testIds={{
            root: 'assign-random-dialog',
            confirm: 'assign-random-confirm-button',
            cancel: 'assign-random-cancel-button',
          }}
          onConfirm={this.onConfirmAssignRandom}
          onClose={this.onCancelAssignRandom}
        />
      </div>
    );
  }
}

LocationsUnassigned.propTypes = {
  /** Show locations or companies */
  type: PropTypes.oneOf([
    'locations',
    'companies',
  ]).isRequired,
  /** Company ID */
  companyId: PropTypes.string.isRequired,
  partnerId: PropTypes.string,
  locations: PropTypes.arrayOf(PropTypes.object),
  /** Locations to use as a limit when querying the API for available locations */
  scopeLimit: PropTypes.arrayOf(PropTypes.object),
  /** Resolutions to use as a limit when querying the API for available locations */
  resolutionLimit: PropTypes.arrayOf(PropTypes.shape({
    size: PropTypes.arrayOf(PropTypes.number),
    rotation: PropTypes.number,
  })),
  /** Custom API endpoint to fetch locations */
  fetchUnassignedLocations: PropTypes.func,
  /** Custom API endpoint to fetch companies */
  fetchUnassignedCompanies: PropTypes.func,
  /** Custom filters for the API endpoint on unasssigned locations */
  filtersUnassigned: PropTypes.object,
  /** Allow a random sample of the available locations to be assigned */
  allowRandom: PropTypes.bool,
  /** When an item is added */
  onAddLocation: PropTypes.func,
  onAddCompany: PropTypes.func,
  /** When every assigned location is replaced by a new set */
  onReplaceLocations: PropTypes.func,
  /** When an item is removed */
  onRemoveLocation: PropTypes.func,
  onRemoveCompany: PropTypes.func,
  /** Update the object data */
  onUpdate: PropTypes.func,
};

LocationsUnassigned.defaultProps = {
  partnerId: '',
  locations: [],
  scopeLimit: [],
  resolutionLimit: [],
  fetchUnassignedLocations: null,
  fetchUnassignedCompanies: null,
  filtersUnassigned: {},
  allowRandom: false,
  onAddLocation: () => {},
  onAddCompany: () => {},
  onReplaceLocations: () => {},
  onRemoveLocation: () => {},
  onRemoveCompany: () => {},
  onUpdate: () => {},
};

function mapStateToProps(state) {
  return {
    user: state.login.user,
  };
}

const mapDispatchToProps = {
  setPanel,
  queueToast,
};

export default connect(mapStateToProps, mapDispatchToProps)(LocationsUnassigned);
